/**
 * DebugRecorderPanel -- Debug recording UI for capturing, annotating, and
 * exporting structured debug log sessions.
 *
 * Accessible via the Debug menu when debug mode is enabled.
 * Styled in the green/black debug theme consistent with DebugConsolePanel.
 */

import { Circle, Download, Square, Trash2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useStore } from "../app/store.ts";
import type { LogCategory } from "../debug/debugLogger.ts";
import {
  type DebugRecording,
  deleteRecording,
  downloadExport,
  exportAll,
  exportRecording,
  getActiveRecordingId,
  getFilteredSize,
  getRecordingSize,
  getSnapshot,
  isRecording,
  setDescription,
  startRecording,
  stopRecording,
  subscribe,
  toggleCategory,
} from "../debug/debugRecorder.ts";
import { Button } from "./Button.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import { useTransientFeedback } from "./shared.tsx";
import {
  COLOR_DEBUG_OVERLAY_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_TINY,
  RADIUS_DOT,
  RADIUS_XS,
  Z_DEBUG,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

const CATEGORIES: LogCategory[] = ["js", "gpu", "wasm"];
const CAT_COLORS: Record<LogCategory, string> = {
  js: "#0f0",
  gpu: "#0af",
  wasm: "#f80",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugRecorderPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.recorder);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-recorder", () =>
    togglePanel("recorder"),
  );

  const recordings = useSyncExternalStore(subscribe, getSnapshot);
  const [, forceUpdate] = useState(0);

  // Tick every second while recording to update elapsed time
  useEffect(() => {
    if (!isRecording()) return;
    const id = setInterval(() => forceUpdate((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const handleStart = useCallback(() => {
    startRecording();
  }, []);

  const handleStop = useCallback(() => {
    stopRecording();
  }, []);

  if (!debugEnabled || !panelOpen) return null;

  const activeId = getActiveRecordingId();
  const recording = isRecording();

  return (
    <DraggablePanel
      id="debug-recorder"
      defaultPosition={{ bottom: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      resizable
      defaultSize={{ width: 380, height: 400 }}
      minSize={{ width: 280, height: 180 }}
      maxSize={{ width: 600, height: 700 }}
      selectionColor="#ff0"
      tint="rgba(0,255,0,0.06)"
      style={{ background: COLOR_DEBUG_OVERLAY_BG }}
      {...sel}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          <span style={{ color: "#0f0", fontWeight: "bold", marginRight: 4 }}>
            RECORDER
          </span>
          {recording && (
            <span
              style={{
                color: "#f44",
                fontSize: 9,
                animation: "pulse 1s ease-in-out infinite",
              }}
            >
              REC
            </span>
          )}
          <span
            style={{ color: COLOR_TEXT_DIM, fontSize: 9, marginLeft: "auto" }}
          >
            {recordings.length} recording{recordings.length !== 1 ? "s" : ""}
          </span>
        </div>
      }
    >
      <div
        style={{
          background: COLOR_DEBUG_OVERLAY_BG,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderRadius: "0 0 6px 6px",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 6px",
            borderBottom: "1px solid rgba(0,255,0,0.12)",
            flexShrink: 0,
          }}
        >
          <RecorderButton
            active={recording}
            onClick={recording ? handleStop : handleStart}
            title={recording ? "Stop recording" : "Start recording"}
            color={recording ? "#f44" : "#0f0"}
          >
            {recording ? <Square size={10} /> : <Circle size={10} />}
            <span>{recording ? "Stop" : "Record"}</span>
          </RecorderButton>

          {recording && activeId && (
            <span
              style={{
                color: COLOR_TEXT_DIM,
                fontFamily: "monospace",
                fontSize: 9,
                marginLeft: 4,
              }}
            >
              {formatElapsed(
                Date.now() -
                  (recordings.find((r) => r.id === activeId)?.startTs ??
                    Date.now()),
              )}
              {" \u00b7 "}
              {recordings.find((r) => r.id === activeId)?.entries.length ?? 0}{" "}
              entries
            </span>
          )}

          <div style={{ flex: 1 }} />

          {recordings.length > 0 && (
            <RecorderButton
              active={false}
              onClick={() => {
                const content = exportAll("text");
                if (content)
                  downloadExport(content, "debug-recordings.txt", "text");
              }}
              title="Export all recordings as text"
              color="#0f0"
            >
              <Download size={10} />
              <span>Export All</span>
            </RecorderButton>
          )}
        </div>

        {/* Recording list */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
          }}
        >
          {recordings.length === 0 ? (
            <div
              style={{
                color: COLOR_TEXT_FAINT,
                fontFamily: "monospace",
                fontSize: FONT_SIZE_TINY,
                padding: "12px 10px",
              }}
            >
              No recordings yet. Press Record to start capturing.
            </div>
          ) : (
            [...recordings]
              .reverse()
              .map((rec) => (
                <RecordingCard
                  key={rec.id}
                  recording={rec}
                  isActive={rec.id === activeId}
                />
              ))
          )}
        </div>
      </div>

      {/* Pulse animation for REC indicator */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </DraggablePanel>
  );
}

// ---------------------------------------------------------------------------
// RecordingCard
// ---------------------------------------------------------------------------

function RecordingCard({
  recording,
  isActive,
}: {
  recording: DebugRecording;
  isActive: boolean;
}) {
  const [desc, setDesc] = useState(recording.description);
  const inputRef = useRef<HTMLInputElement>(null);
  const [deleteFlash, fireDeleteFlash] = useTransientFeedback(400);

  // Sync local state with recording description
  useEffect(() => {
    setDesc(recording.description);
  }, [recording.description]);

  const handleDescBlur = useCallback(() => {
    setDescription(recording.id, desc);
  }, [recording.id, desc]);

  const handleDescKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        setDescription(recording.id, desc);
        (e.target as HTMLInputElement).blur();
      }
      e.stopPropagation();
    },
    [recording.id, desc],
  );

  const fullSize = getRecordingSize(recording.id);
  const filteredSize = getFilteredSize(recording.id);
  const allCatsEnabled = recording.enabledCategories.size === CATEGORIES.length;

  // Count entries by category
  const catCounts: Record<string, number> = { js: 0, gpu: 0, wasm: 0 };
  for (const e of recording.entries) {
    catCounts[e.cat] = (catCounts[e.cat] || 0) + 1;
  }

  return (
    <div
      style={{
        margin: "4px 6px",
        padding: "6px 8px",
        border: isActive
          ? "1px solid rgba(255,68,68,0.4)"
          : "1px solid rgba(0,255,0,0.12)",
        borderRadius: RADIUS_XS,
        background: isActive ? "rgba(255,68,68,0.05)" : "rgba(0,255,0,0.03)",
      }}
    >
      {/* Header: time + size */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "monospace",
          fontSize: 9,
          color: COLOR_TEXT_DIM,
          marginBottom: 4,
        }}
      >
        <span>
          {formatTime(recording.startTs)}
          {recording.endTs
            ? ` \u2013 ${formatTime(recording.endTs)}`
            : " \u2013 …"}
          {" \u00b7 "}
          {recording.entries.length} entries
        </span>
        <span>
          {allCatsEnabled ? (
            formatSize(fullSize)
          ) : (
            <>
              {formatSize(filteredSize)}
              <span style={{ color: COLOR_TEXT_FAINT }}>
                {" "}
                (full: {formatSize(fullSize)})
              </span>
            </>
          )}
        </span>
      </div>

      {/* Description input */}
      <input
        ref={inputRef}
        data-no-drag
        type="text"
        placeholder="Describe the problem…"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        onBlur={handleDescBlur}
        onKeyDown={handleDescKeyDown}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "3px 6px",
          marginBottom: 4,
          background: "rgba(0,0,0,0.4)",
          border: "1px solid rgba(0,255,0,0.15)",
          borderRadius: RADIUS_DOT,
          color: "#0f0",
          fontFamily: "monospace",
          fontSize: FONT_SIZE_TINY,
          outline: "none",
        }}
      />

      {/* Category toggles + actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        {CATEGORIES.map((cat) => {
          const enabled = recording.enabledCategories.has(cat);
          const count = catCounts[cat] || 0;
          return (
            <CategoryToggle
              key={cat}
              cat={cat}
              enabled={enabled}
              count={count}
              onClick={() => toggleCategory(recording.id, cat)}
            />
          );
        })}

        <div style={{ flex: 1 }} />

        <RecorderButton
          active={false}
          onClick={() => {
            const content = exportRecording(recording.id, "text");
            if (content) {
              const safeName = (recording.description || "recording")
                .replace(/[^a-zA-Z0-9_-]/g, "_")
                .slice(0, 40);
              downloadExport(content, `debug-${safeName}.txt`, "text");
            }
          }}
          title="Export recording as text"
          color="#0f0"
        >
          <Download size={10} />
          <span>Export</span>
        </RecorderButton>

        <RecorderButton
          active={false}
          onClick={() => {
            if (deleteFlash) return;
            fireDeleteFlash();
            deleteRecording(recording.id);
          }}
          title="Delete recording"
          color="#f44"
        >
          <Trash2 size={10} />
        </RecorderButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryToggle
// ---------------------------------------------------------------------------

function CategoryToggle({
  cat,
  enabled,
  count,
  onClick,
}: {
  cat: LogCategory;
  enabled: boolean;
  count: number;
  onClick: () => void;
}) {
  const color = CAT_COLORS[cat];
  return (
    <Button
      data-no-drag
      onClick={onClick}
      title={`${enabled ? "Exclude" : "Include"} ${cat.toUpperCase()} entries in export (${count})`}
      variant="ghost"
      size="xs"
      style={{
        gap: 3,
        padding: "1px 5px",
        borderRadius: RADIUS_DOT,
        border: `1px solid ${enabled ? color : "rgba(255,255,255,0.1)"}`,
        background: enabled ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        color: enabled ? color : COLOR_TEXT_FAINT,
        fontFamily: "monospace",
        fontSize: 8,
        opacity: enabled ? 1 : 0.5,
        lineHeight: 1.3,
      }}
    >
      {cat.toUpperCase()}
      <span style={{ fontSize: 7, opacity: 0.7 }}>{count}</span>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// RecorderButton (reusable toolbar button)
// ---------------------------------------------------------------------------

function RecorderButton({
  active,
  disabled,
  onClick,
  title,
  color = "#0f0",
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      data-no-drag
      onClick={onClick}
      disabled={disabled}
      title={title}
      variant="ghost"
      size="xs"
      style={{
        gap: 3,
        padding: "3px 7px",
        borderRadius: RADIUS_XS,
        border: "1px solid " + (active ? `${color}66` : `${color}26`),
        background: active ? `${color}26` : "transparent",
        color: disabled ? `${color}40` : color,
        fontFamily: "monospace",
        fontSize: 9,
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Button>
  );
}
